import { useEffect, useMemo, useState } from 'react'
import {
    getWebhooks, getWebhookEvents, createWebhook, updateWebhook, deleteWebhook, testWebhook, revealWebhookSecret,
    getInboundWebhooks, setupInboundWebhook,
    type Webhook, type WebhookEventType, type InboundEndpoint,
} from '../api'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ConfirmDialog from '../components/ConfirmDialog'
import Drawer from '../components/Drawer'
import InfoTip from '../components/InfoTip'
import PasswordInput from '../components/PasswordInput'
import { getVpDeliveries, type VpDeliveryRow } from '../api'
import { checkWithin } from '../forms/checkForm'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { fill, PAGES_WEBHOOKS_MANAGER_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />
const SendIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
)

type View = 'out' | 'in'
type DrawerState = { id: string | null, kind: string, name: string, url: string, eventTypes: string[], secret: string, enabled: boolean, secretSet: boolean }

export default function WebhooksManager() {
    const { can } = useAuth()
    const { toast } = useToast()
    const canWrite = can('webhooks', 'write')
    const canDelete = can('webhooks', 'delete')

    const [view, setView] = useState<View>('out')
    const [hooks, setHooks] = useState<Webhook[]>([])
    const [events, setEvents] = useState<WebhookEventType[]>([])
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [inbound, setInbound] = useState<InboundEndpoint[]>([])
    const [inboundBusy, setInboundBusy] = useState<string | null>(null)

    const load = () => getWebhooks().then(setHooks)
    const loadInbound = () => getInboundWebhooks().then(setInbound)
    useEffect(() => {
        load().catch((e) => setError(e?.response?.data?.error ?? e?.message ?? T.loadFailed))
        getWebhookEvents().then(setEvents).catch(() => {})
        loadInbound().catch(() => {})
    }, [])

    const eventLabel = (v: string) => events.find((e) => e.value === v)?.label ?? v

    async function run(fn: () => Promise<void>) {
        setBusy(true); setError('')
        try { await fn() }
        catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? T.error) }
        finally { setBusy(false) }
    }

    // --- Editor drawer (new + edit) ---
    const [drawer, setDrawer] = useState<DrawerState | null>(null)
    const openNew = () => setDrawer({ id: null, kind: 'generic', name: '', url: '', eventTypes: [], secret: '', enabled: true, secretSet: false })
    const openEdit = (r: Webhook) => {
        setDrawer({ id: r._id, kind: r.kind, name: r.name, url: r.url, eventTypes: r.eventTypes ?? [], secret: '', enabled: r.enabled, secretSet: r.secretSet })
        // Prefill the real stored secret so the eye toggle can reveal it for comparison.
        if (r.secretSet) {
            revealWebhookSecret(r._id)
                .then((s) => setDrawer((d) => (d && d.id === r._id ? { ...d, secret: s } : d)))
                .catch(() => {})
        }
    }
    const closeDrawer = () => { if (!busy) { setDrawer(null); setError('') } }
    const isConverter = drawer?.kind === 'converter'
    const set = <K extends keyof DrawerState>(key: K, value: DrawerState[K]) => setDrawer((d) => (d ? { ...d, [key]: value } : d))
    // Toggle one event in the multi-select list.
    const toggleEvent = (value: string) => setDrawer((d) => {
        if (!d) return d
        const has = d.eventTypes.includes(value)
        return { ...d, eventTypes: has ? d.eventTypes.filter((e) => e !== value) : [...d.eventTypes, value] }
    })

    const saveDrawer = (e?: { currentTarget: EventTarget | null }) => {
        if (!drawer || !checkWithin(e, '.drawer')) return
        run(async () => {
            if (drawer.id === null) {
                await createWebhook({ name: drawer.name.trim(), url: drawer.url.trim(), eventTypes: drawer.eventTypes, secret: drawer.secret, enabled: drawer.enabled })
            } else {
                const patch: Record<string, unknown> = { name: drawer.name.trim(), url: drawer.url.trim(), enabled: drawer.enabled }
                if (drawer.kind !== 'converter') patch.eventTypes = drawer.eventTypes // converter event is fixed
                if (drawer.secret) patch.secret = drawer.secret
                await updateWebhook(drawer.id, patch)
            }
            setDrawer(null); await load()
        })
    }

    const toggle = (r: Webhook) => run(async () => { await updateWebhook(r._id, { enabled: !r.enabled }); await load() })
    const test = (r: Webhook) => run(async () => {
        const res = await testWebhook(r._id)
        toast(res.ok ? fill(T.testOk, { status: res.status }) : fill(T.testFailed, { reason: res.error || res.status }))
        await load()
    })

    // --- Delete ---
    const [del, setDel] = useState<Webhook | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    async function confirmDelete() {
        if (!del) return
        setDelBusy(true); setDelError('')
        try { await deleteWebhook(del._id); setDel(null); await load() }
        catch (e: any) { setDelError(e?.response?.data?.error ?? T.deleteFailed) }
        finally { setDelBusy(false) }
    }

    // --- Client-side search + pagination (hooks are few) ---
    const [q, setQ] = useState('')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase()
        return s ? hooks.filter((h) => h.name.toLowerCase().includes(s) || h.url.toLowerCase().includes(s) || h.eventTypes.some((e) => eventLabel(e).toLowerCase().includes(s))) : hooks
    }, [hooks, q, events])
    const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
    const shownPage = Math.min(page, pageCount)
    const paged = filtered.slice((shownPage - 1) * pageSize, shownPage * pageSize)

    function statusCell(h: Webhook) {
        if (h.lastStatus == null && !h.lastAttemptAt) return <span className="muted">—</span>
        const ok = h.lastStatus != null && h.lastStatus >= 200 && h.lastStatus < 300
        return <span title={h.lastError || ''} style={{ color: ok ? 'var(--ok)' : 'var(--warn)' }}>{h.lastStatus ?? T.error}</span>
    }

    const columns: Column<Webhook>[] = [
        {
            key: 'name', header: T.colName, defaultWidth: 220,
            render: (r) => <>{r.name}{r.kind === 'converter' && <span className="badge finalized" style={{ marginLeft: 6 }}>{T.converter}</span>}</>,
        },
        { key: 'eventType', header: T.colEvents, sortField: 'eventType', defaultWidth: 180, render: (r) => (r.eventTypes.length ? r.eventTypes.map(eventLabel).join(', ') : <span className="muted">—</span>) },
        { key: 'url', header: T.colUrl, defaultWidth: 280, render: (r) => <span style={{ wordBreak: 'break-all' }}>{r.url}</span> },
        { key: 'secret', header: T.colSecret, defaultWidth: 110, render: (r) => (r.secretSet ? '••••' : <span className="muted">—</span>) },
        {
            key: 'enabled', header: T.colEnabled, defaultWidth: 70,
            render: (r) => <label className="switch"><input type="checkbox" checked={r.enabled} disabled={!canWrite || busy} onChange={() => toggle(r)} /></label>,
        },
        { key: 'lastStatus', header: T.colStatus, defaultWidth: 90, render: (r) => statusCell(r) },
        {
            key: 'actions', header: T.colActions, defaultWidth: 130, sticky: true,
            render: (r) => (
                <div className="row-actions">
                    {canWrite && <button className="icon-action" title={T.sendTest} aria-label={T.sendTest} disabled={busy} onClick={() => test(r)}><SendIcon /></button>}
                    {canWrite && <button className="icon-action" title={T.edit} aria-label={T.edit} onClick={() => openEdit(r)}><EditIcon /></button>}
                    {canDelete && r.kind !== 'converter' && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => setDel(r)}><TrashIcon /></button>}
                    {!canWrite && !canDelete && <span className="muted">—</span>}
                </div>
            ),
        },
    ]

    // --- Inbound (active-PSP endpoint per brand; setup only applies to Stripe) ---
    async function setupInbound(brand: string) {
        setInboundBusy(brand); setError('')
        try {
            const r = await setupInboundWebhook(brand)
            toast(`${r.message} (${r.mode})`)
            await loadInbound()
        } catch (e: any) {
            setError(e?.response?.data?.error ?? e?.message ?? T.setupFailed)
        } finally {
            setInboundBusy(null)
        }
    }

    return (
        <div className="list-page">
            <ListToolbar title={T.title}
                infoText={T.info}
                action={canWrite && view === 'out' ? <button type="button" onClick={openNew}>{T.newWebhookButton}</button> : undefined} />

            {error && <div className="notice err">{error}</div>}

            <div className="pill-tabs" role="tablist">
                <button type="button" role="tab" className={`pill-tab${view === 'out' ? ' active' : ''}`}
                    aria-selected={view === 'out'} onClick={() => setView('out')}>
                    {T.outgoing}{hooks.length ? ` (${hooks.length})` : ''}
                </button>
                <button type="button" role="tab" className={`pill-tab${view === 'in' ? ' active' : ''}`}
                    aria-selected={view === 'in'} onClick={() => setView('in')}>
                    {T.incoming}{inbound.length ? ` (${inbound.length})` : ''}
                </button>
            </div>

            {view === 'out' && <>
                <ListPanel
                    count={filtered.length}
                    grandTotal={hooks.length}
                    filtered={q.trim() !== ''}
                    search={{ value: q, onChange: (v) => { setQ(v); setPage(1) }, placeholder: T.searchPlaceholder }}
                >
                    {filtered.length === 0 ? (
                        <p className="muted">{q ? T.noneFound : T.noneYet}</p>
                    ) : (
                        <DataTable tableId="webhooks-out" columns={columns} rows={paged} rowKey={(r) => r._id} />
                    )}
                </ListPanel>
                <Pagination page={shownPage} pageCount={pageCount} onPage={setPage}
                    pageSize={pageSize} onPageSize={(n) => { setPageSize(n); setPage(1) }} />
                <VpDeliveries />
            </>}

            {view === 'in' && (
                <div className="panel">
                    <h2>{T.inboundHeading}</h2>
                    <p className="muted" style={{ marginTop: 0 }}>
                        {T.inboundIntro1}<strong>{T.inboundIntroActive}</strong>
                        {' '}{T.inboundIntro2}<strong>{T.stripe}</strong>{T.inboundIntro3}<strong>{T.mollie}</strong>{T.inboundIntro4}
                    </p>
                    <div className="table-scroll">
                        <table className="fixed">
                            <thead>
                                <tr>
                                    <th>{T.colBrand}</th><th style={{ width: 90 }}>{T.colPsp}</th><th>{T.colPath}</th>
                                    <th style={{ width: 80 }}>{T.colMode}</th>
                                    <th style={{ width: 110 }}>{T.colSecret}</th><th style={{ width: 110 }}>{T.colRegistered}</th>
                                    <th style={{ width: 130 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {inbound.map((e) => {
                                    const isStripe = e.provider === 'stripe'
                                    const secretSet = e.testmode ? e.testSecretSet : e.liveSecretSet
                                    const endpointSet = e.testmode ? e.testEndpointSet : e.liveEndpointSet
                                    return (
                                        <tr key={e.brand}>
                                            <td>{e.name}</td>
                                            <td><span className="badge">{isStripe ? T.stripe : T.mollie}</span></td>
                                            <td style={{ wordBreak: 'break-all' }}><code>{e.path}</code></td>
                                            <td>{e.testmode ? T.modeTest : T.modeLive}</td>
                                            {isStripe ? (
                                                <>
                                                    <td style={{ color: secretSet ? 'var(--ok)' : 'var(--muted)' }}>{secretSet ? T.secretSet : '—'}</td>
                                                    <td style={{ color: endpointSet ? 'var(--ok)' : 'var(--muted)' }}>{endpointSet ? T.yes : '—'}</td>
                                                    <td>
                                                        {canWrite && (
                                                            <button type="button" className="secondary" disabled={inboundBusy === e.brand}
                                                                onClick={() => setupInbound(e.brand)}>
                                                                {inboundBusy === e.brand ? '…' : T.setup}
                                                            </button>
                                                        )}
                                                    </td>
                                                </>
                                            ) : (
                                                <td colSpan={3} className="muted">{T.noSetupNeeded}</td>
                                            )}
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Drawer
                open={!!drawer}
                title={drawer?.id ? T.editWebhook : T.newWebhook}
                width={460}
                onClose={closeDrawer}
                footer={drawer ? (
                    <>
                        <button type="button" onClick={(e) => saveDrawer(e)} disabled={busy}>
                            {busy ? '…' : (drawer.id ? T.save : T.create)}
                        </button>
                        <button type="button" className="secondary" onClick={closeDrawer} disabled={busy}>{T.cancel}</button>
                    </>
                ) : undefined}>
                {drawer && <>
                    <div className="field">
                        <label>{T.colName}</label>
                        <input type="text" autoFocus placeholder={T.namePlaceholder} required
                            value={drawer.name} onChange={(e) => set('name', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>{T.colUrl}</label>
                        <input type="url" placeholder={T.urlPlaceholder} required value={drawer.url} onChange={(e) => set('url', e.target.value)} />
                    </div>
                    <div className="field">
                        <label>{T.colEvents} <InfoTip>{T.eventsTip}</InfoTip></label>
                        {isConverter ? (
                            <input type="text" disabled value={drawer.eventTypes.map(eventLabel).join(', ')} />
                        ) : (
                            <div className="checkbox-list" role="group" aria-label={T.colEvents}>
                                {events.map((ev, i) => (
                                    <label key={ev.value} className="switch" style={{ display: 'block' }}>
                                        {/* At least one event: the first box stands for the group in the form check. */}
                                        <input type="checkbox" checked={drawer.eventTypes.includes(ev.value)}
                                            data-required={i === 0 ? '' : undefined}
                                            data-filled={i === 0 ? String(drawer.eventTypes.length > 0) : undefined}
                                            onChange={() => toggleEvent(ev.value)} />
                                        {ev.label}
                                    </label>
                                ))}
                                {events.length === 0 && <span className="muted">{T.noEvents}</span>}
                            </div>
                        )}
                    </div>
                    <div className="field">
                        <label>{T.colSecret} <InfoTip>{T.secretTip}</InfoTip></label>
                        <PasswordInput autoComplete="off" placeholder={drawer.secretSet ? T.keepUnchanged : ''}
                            value={drawer.secret} onChange={(v) => set('secret', v)} />
                    </div>
                    <div className="field">
                        <label className="switch">
                            <input type="checkbox" checked={drawer.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                            {T.colEnabled}
                        </label>
                    </div>
                </>}
            </Drawer>

            <ConfirmDialog
                open={!!del}
                title={T.deleteTitle}
                message={del ? <>{fill(T.deleteConfirm, { name: del.name })}</> : null}
                confirmLabel={T.delete}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={() => { if (!delBusy) { setDel(null); setDelError('') } }}
            />
        </div>
    )
}

// Zustellungen an vitalpilze, die nicht glatt durchgingen. ⛔ Vor allem die ENDGUELTIG
// abgelehnten (421): Die Gegenseite ist die falsche Umgebung (dev ↔ prod) — diese Meldungen werden
// nie nachgefasst, und ohne diese Anzeige stuenden sie nur im Protokoll
// (communication/backoffice-vitalpilze-radar-umgebungstrennung.md).
function VpDeliveries() {
    const D = T.deliveries
    const [d, setD] = useState<Awaited<ReturnType<typeof getVpDeliveries>> | null>(null)
    useEffect(() => { getVpDeliveries().then(setD).catch(() => setD(null)) }, [])
    if (!d) return null
    const zeit = (v?: string | null) => (v ? new Date(v).toLocaleString('de-DE') : '—')
    const liste = (titel: string, rows: VpDeliveryRow[], wann: (r: VpDeliveryRow) => string | null | undefined, farbe?: string) => rows.length > 0 && (
        <div style={{ marginTop: 12 }}>
            <b style={farbe ? { color: farbe } : undefined}>{titel} ({rows.length})</b>
            <table className="table" style={{ marginTop: 4 }}>
                <thead><tr><th>{D.colWhen}</th><th>{D.colEvent}</th><th>{D.colLabel}</th><th>{D.colAttempts}</th><th>{D.colReason}</th></tr></thead>
                <tbody>{rows.map((r) => (
                    <tr key={r.id}><td>{zeit(wann(r))}</td><td>{r.event}</td><td>{r.label}</td><td>{r.attempts}</td><td>{r.lastError || '—'}</td></tr>
                ))}</tbody>
            </table>
        </div>
    )
    return (
        <div className="panel" style={{ marginTop: 16 }}>
            <h3 style={{ marginTop: 0 }}>{D.heading} <InfoTip>
                {D.tip1}<b>{D.tipOpen}</b>{D.tip2}<b>{D.tipGaveUp}</b>{D.tip3}<b>{D.tipRejected}</b>{D.tip4}
            </InfoTip></h3>
            <p className="muted" style={{ margin: 0 }}>
                {fill(D.counts, { delivered: d.counts.delivered ?? 0, pending: d.counts.pending ?? 0, superseded: d.counts.superseded ?? 0, gaveUp: d.counts.gaveup ?? 0, rejected: d.counts.rejected ?? 0 })}
            </p>
            {liste(D.listRejected, d.abgelehnt, (r) => r.rejectedAt, 'var(--danger)')}
            {liste(D.listGaveUp, d.aufgegeben, (r) => r.gaveUpAt)}
            {liste(D.listOpen, d.offen, (r) => r.firstFailedAt)}
        </div>
    )
}
